import config from "@/lib/config";
import { defaultMetatags } from "@/lib/constants";
import deepmerge from "deepmerge";
import Head from "next/head";
import { useRouter } from "next/router";
import parseUrl from "parse-url";
import { useMemo } from "react";

function mergeMetatags(defaultMetatags, metatags) {
  if (!metatags) return defaultMetatags;
  const tags = deepmerge(defaultMetatags, metatags);
  // ogキーとtwitterキーにのtitle, descriptionキーにmetatagsのtitleとdescriptionを代入している（metaタグとogpタグでtitle, descriptionを重複させてる）（これやらないとdefaultMetatagsの内容になってしまう）
  if (!metatags?.og?.title && metatags?.title) {
    tags.og.title = metatags.title;
  }
  if (!metatags?.og?.description && metatags?.description) {
    tags.og.description = metatags.description;
  }
  if (!metatags?.twitter?.title && metatags?.title) {
    tags.twitter.title = metatags.title;
  }
  if (!metatags?.twitter?.description && metatags?.description) {
    tags.twitter.description = metatags.description;
  }
  return tags;
}

export default function DefaultHeader({ metatags }) {
  const router = useRouter();
  const canonicalUrl = useMemo(() => {
    if (metatags?.canonicalUrl) {
      return metatags?.canonicalUrl;
    } else {
      const host = config.host["vod"];
      const parsed = parseUrl(`${host}${router.asPath}`);
      return `${host}${parsed.pathname}`;
    }
  }, [router]);

  const [path, themeColor, tags] = useMemo(() => {
    return ["/icon", "#feff00", mergeMetatags(defaultMetatags.vod, metatags)];
  }, [metatags]);

  return (
    <Head>
      <title key="title">{metatags?.title || tags.title}</title>
      <link rel="canonical" href={canonicalUrl} />
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, user-scalable=no"
      />
      <meta name="keywords" content={tags.keywords} key="keywords" />
      <meta name="description" content={tags.description} key="description" />
      <meta property="og:title" content={tags.og.title} key="ogtitle" />
      <meta property="og:type" content={tags.og.type} key="ogtype" />
      <meta property="og:url" content={tags.og.url} key="ogurl" />
      <meta property="og:image" content={tags.og.image} key="ogimage" />
      <meta
        property="og:site_name"
        content={tags.og.site_name}
        key="ogsite_name"
      />
      <meta
        property="og:description"
        content={tags.og.description}
        key="ogdescription"
      />
      <meta name="twitter:card" content={tags.twitter.card} key="twittercard" />
      <meta
        name="twitter:title"
        content={tags.twitter.title}
        key="twittertitle"
      />
      <meta
        name="twitter:description"
        content={tags.twitter.description}
        key="twitterdescription"
      />
      <meta
        name="twitter:image"
        content={tags.twitter.image}
        key="twitterimage"
      />
      <meta name="application-name" content={tags.appName} />
      <meta
        name="msapplication-square70x70logo"
        content={`${path}/site-tile-70x70.png`}
      />
      <meta
        name="msapplication-square150x150logo"
        content={`${path}/site-tile-150x150.png`}
      />
      <meta
        name="msapplication-wide310x150logo"
        content={`${path}/site-tile-310x150.png`}
      />
      <meta
        name="msapplication-square310x310logo"
        content={`${path}/site-tile-310x310.png`}
      />
      <meta name="msapplication-TileColor" content={themeColor} />

      <meta name="theme-color" content={themeColor} />

      <meta name="apple-itunes-app" content="app-id=1666759583" />

      <link rel="shortcut icon" href={`${path}/favicon.ico`} />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-16x16.png`}
        sizes="16x16"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-24x24.png`}
        sizes="24x24"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-32x32.png`}
        sizes="32x32"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-36x36.png`}
        sizes="36x36"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-48x48.png`}
        sizes="48x48"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-72x72.png`}
        sizes="72x72"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-96x96.png`}
        sizes="96x96"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-16x16.png`}
        sizes="16x16"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-128x128.png`}
        sizes="128x128"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-144x144.png`}
        sizes="144x144"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-152x152.png`}
        sizes="152x152"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-160x160.png`}
        sizes="160x160"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-192x192.png`}
        sizes="192x192"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-196x196.png`}
        sizes="196x196"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-256x256.png`}
        sizes="256x256"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-384x384.png`}
        sizes="384x384"
      />
      <link
        rel="icon"
        type="image/png"
        href={`${path}/icon-512x512.png`}
        sizes="512x512"
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="57x57"
        href={`${path}/apple-touch-icon-57x57.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="60x60"
        href={`${path}/apple-touch-icon-60x60.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="72x72"
        href={`${path}/apple-touch-icon-72x72.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="76x76"
        href={`${path}/apple-touch-icon-76x76.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="114x114"
        href={`${path}/apple-touch-icon-114x114.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="120x120"
        href={`${path}/apple-touch-icon-120x120.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="144x144"
        href={`${path}/apple-touch-icon-144x144.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="152x152"
        href={`${path}/apple-touch-icon-152x152.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        sizes="180x180"
        href={`${path}/apple-touch-icon-180x180.png`}
      />
      <link
        rel="apple-touch-icon-precomposed"
        href={`${path}/apple-touch-icon.png`}
      />
    </Head>
  );
}
